import { SiWordpress } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { WebPublisherResult } from 'sprites-types/workflow/tools/webPublisher'
import type { WordPressWebPublisherArgs } from 'sprites-types/workflow/tools/wordpressWebPublisher'
import { wordpressWebPublisher } from 'sprites-types/workflow/tools/wordpressWebPublisher'
import { BaseWebPublisherDisplay } from '../shared/BaseWebPublisherDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

const getActionDisplayName = (action: string) => {
  switch (action) {
    case 'create':
      return 'Create Content'
    case 'update':
      return 'Update Content'
    case 'subagentTaskRunner':
      return 'Natural Language Task'
    case 'getContent':
      return 'Get Content'
    case 'listContent':
      return 'List Content'
    case 'publishContent':
      return 'Publish Content'
    case 'updateContent':
      return 'Update Content'
    case 'uploadMedia':
      return 'Upload Media'
    case 'getMedia':
      return 'Get Media'
    case 'updateMedia':
      return 'Update Media'
    case 'listCategories':
      return 'List Categories'
    case 'listTags':
      return 'List Tags'
    default:
      return action
  }
}

function WordPressWebPublisherDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const inputs = wordpressWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return <SchemaValidationError error={inputs.error} nodeExecution={nodeExecution} />
  }

  const schema = getToolResultSchema(wordpressWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as WordPressWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)

  return (
    <BaseWebPublisherDisplay
      data={data}
      platformName="WordPress"
      platformIcon={<SiWordpress className="h-4 w-4 text-[#21759b]" />}
      platformColor="text-[#21759b]"
      backgroundColor="bg-blue-50"
      actionName={actionDisplayName}
    />
  )
}

async function downloadWordPressWebPublisher(_nodeExecution: NodeExecution): Promise<void> {
  // TODO: Implement download functionality
  console.log('Download not implemented yet for WordPress Web Publisher')
}

function wordpressWebPublisherToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = wordpressWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return `## WordPress Web Publisher Error\n\nFailed to parse inputs: ${inputs.error.message}`
  }

  const schema = getToolResultSchema(wordpressWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return `## WordPress Web Publisher Error\n\nFailed to parse outputs: ${parseResult.error.message}`
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as WordPressWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)

  return `## WordPress ${actionDisplayName}

**Action:** ${action}

### Results

${data.summary}

${data.url ? `**URL:** ${data.url}\n` : ''}
${
  data.changes && data.changes.length > 0
    ? `### Changes Made

${data.changes.map((change) => `- **${change.action}** ${change.target}: ${change.details}`).join('\n')}
`
    : ''
}
---
`
}

export const WordPressWebPublisherDisplay = createWorkflowToolComponent(
  WordPressWebPublisherDisplayComponent,
  downloadWordPressWebPublisher,
  wordpressWebPublisherToMarkdown
)
